import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/comics';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51漫画网 - 韩国漫画阅读平台',
    description: '51漫画网品牌介绍，了解我们的使命、优势以及联系方式。51漫画网提供无删减韩国漫画下拉式阅读服务。',
    url: 'https://www.51manhua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '51漫画网',
      url: 'https://www.51manhua.com',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.51manhua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51漫画网',
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    foundingDate: siteInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
];

const teamMembers = [
  {
    name: '李明浩',
    role: '创始人 & CEO',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20headshot%20portrait%2C%20modern%20business%20casual%2C%20warm%20friendly%20smile%2C%20clean%20white%20background%2C%20soft%20natural%20lighting%2C%20corporate%20photography&width=200&height=200&seq=about-avatar-01&orientation=squarish',
    desc: '韩漫爱好者，致力于打造最好的韩漫阅读平台。',
  },
  {
    name: '张晓雅',
    role: '内容总监',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20headshot%20portrait%2C%20modern%20business%20casual%2C%20warm%20friendly%20smile%2C%20clean%20white%20background%2C%20soft%20natural%20lighting%2C%20corporate%20photography&width=200&height=200&seq=about-avatar-02&orientation=squarish',
    desc: '10年漫画行业经验，严格把控内容质量。',
  },
  {
    name: '王思远',
    role: '技术负责人',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20man%20headshot%20portrait%2C%20modern%20tech%20casual%20style%2C%20warm%20friendly%20smile%2C%20clean%20white%20background%2C%20soft%20natural%20lighting%2C%20corporate%20photography&width=200&height=200&seq=about-avatar-03&orientation=squarish',
    desc: '全栈工程师，保障平台稳定流畅的阅读体验。',
  },
  {
    name: '赵雨晴',
    role: '运营经理',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20woman%20headshot%20portrait%2C%20modern%20business%20casual%2C%20warm%20friendly%20smile%2C%20clean%20white%20background%2C%20soft%20natural%20lighting%2C%20corporate%20photography&width=200&height=200&seq=about-avatar-04&orientation=squarish',
    desc: '专注用户运营，让每一位读者都能找到心仪的漫画。',
  },
];

const milestones = [
  { year: '2020', title: '平台创立', desc: '51漫画网正式上线，开启韩漫阅读新时代。' },
  { year: '2021', title: '突破百万用户', desc: '用户数量突破100万，成为韩漫爱好者首选平台。' },
  { year: '2022', title: '漫画数量突破500部', desc: '持续引进优质韩漫资源，内容库不断丰富。' },
  { year: '2023', title: '移动端优化升级', desc: '全面优化下拉式阅读体验，适配各类移动设备。' },
  { year: '2024', title: '用户突破300万', desc: '日活跃用户持续增长，成为中国领先的韩漫平台。' },
  { year: '2025', title: '漫画数量突破1000部', desc: '收录超过1000部韩国漫画，品类覆盖更加全面。' },
  { year: '2026', title: 'AI智能推荐上线', desc: '引入智能推荐算法，为用户精准推荐喜爱的漫画。' },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="关于我们 - 51漫画网 - 韩国漫画阅读平台"
        description="了解51漫画网的品牌故事、发展历程与联系信息。51漫画网提供无删减韩国漫画下拉式阅读服务，免费韩漫大全，更新及时资源优质。"
        keywords="关于51漫画网,韩国漫画平台,免费韩漫,51漫画网联系方式,韩漫阅读"
        canonicalUrl="https://www.51manhua.com/about"
        schemaData={aboutSchema}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="mt-8 text-center py-12 md:py-16">
            <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-900 mb-4">
              关于51漫画网
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              专注韩国漫画阅读，为每一位韩漫爱好者提供最优质的阅读体验
            </p>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">51漫画网</strong>成立于{siteInfo.founded}年，是国内领先的韩国漫画在线阅读平台。我们专注于提供<strong>无删减</strong>、高品质的韩国漫画内容，采用<strong>下拉式阅读</strong>模式，让读者享受流畅沉浸的阅读体验。
                  </p>
                  <p>
                    经过多年的发展，51漫画网已收录<strong>{siteInfo.totalComics}+部</strong>韩国漫画，涵盖热血动作、恋爱言情、悬疑推理、奇幻冒险、搞笑日常等十多个品类，累计服务<strong>{(siteInfo.totalUsers / 10000).toFixed(0)}万+</strong>用户，日均访问量超过<strong>{(siteInfo.dailyVisitors / 10000).toFixed(1)}万</strong>次。
                  </p>
                  <p>
                    我们的使命是让每一位韩漫爱好者都能便捷地获取优质漫画内容。无论你是追更党还是补番党，51漫画网都能满足你的阅读需求。我们与多家韩国漫画平台合作，确保漫画更新及时、资源优质。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden bg-background-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20cozy%20reading%20scene%2C%20person%20reading%20comics%20on%20tablet%20in%20warm%20cafe%2C%20soft%20coral%20and%20amber%20lighting%2C%20artistic%20atmosphere%2C%20clean%20modern%20aesthetic%2C%20no%20text&width=700&height=500&seq=about-brand-img&orientation=landscape"
                  alt="51漫画网 - 韩国漫画阅读体验"
                  title="51漫画网 - 韩国漫画阅读平台品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '无删减内容', desc: '坚持提供原汁原味的韩漫内容，绝不删减任何情节，给你最完整的阅读体验。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '专为移动端优化的下拉式阅读模式，单手操作更便捷，沉浸感更强。' },
                { icon: 'ri-flashlight-line', title: '更新及时', desc: '与韩国原作保持同步更新，让你第一时间追到最新话，快人一步。' },
                { icon: 'ri-price-tag-3-line', title: '完全免费', desc: '所有漫画免费阅读，无需付费订阅，让好内容触手可及。' },
                { icon: 'ri-stack-line', title: '海量资源', desc: '收录1000+部漫画，涵盖十大品类，不断更新扩充内容库。' },
                { icon: 'ri-device-line', title: '全设备适配', desc: '电脑、平板、手机无缝切换，随时随地享受韩漫阅读乐趣。' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 p-5 bg-background-50 rounded-xl">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 shrink-0">
                    <i className={`${item.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="py-10 md:py-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block md:w-1/2" />
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50" />
                    <div className="ml-10 md:ml-0 md:w-1/2 md:px-10">
                      <div className="bg-background-100 rounded-xl p-5">
                        <span className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground-500">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-2xl">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-6 text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-background-200 mb-4">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-500 font-medium mt-0.5 mb-2">
                    {member.role}
                  </p>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {member.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-50 mb-3">
                  <i className="ri-mail-line text-primary-500 text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                  电子邮箱
                </h3>
                <p className="text-sm text-foreground-500">{siteInfo.email}</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-50 mb-3">
                  <i className="ri-time-line text-primary-500 text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                  工作时间
                </h3>
                <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-50 mb-3">
                  <i className="ri-chat-3-line text-primary-500 text-xl"></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">
                  在线反馈
                </h3>
                <p className="text-sm text-foreground-500">通过站内反馈联系我们</p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-10 text-center">
            <div className="bg-background-100 rounded-2xl p-10 md:p-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                加入我们的读者大家庭
              </h2>
              <p className="text-sm text-foreground-500 mb-6 max-w-lg mx-auto">
                与{siteInfo.totalUsers.toLocaleString()}+韩漫爱好者一起，畅享无删减韩国漫画阅读体验
              </p>
              <Link
                to="/"
                className="inline-flex px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm transition-all duration-300 whitespace-nowrap"
              >
                开始阅读
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}